import { Draft, PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import type {
  IInsertScheduledEmailRequest,
  IScheduledEmail,
  IScheduledEmailContentV2,
  ThemePackCampaignEssentials,
} from '@/types/email.types';
import { RootState } from '@/store/root-state';
import { ScheduledEmailsState } from './initial';
import {
  CreateScheduledEmail,
  GetEventScheduledEmails,
  GetEventPublishedScheduledEmails,
  GetChannelScheduledEmails,
  EditScheduledEmail,
  DeleteScheduledEmail,
  PublishScheduledEmail,
} from '@/connection/scheduled-emails/scheduled-emails';
import { defaultCustomizations } from './defaults';
import { scheduleCustomizationsSave } from './customizations-save-scheduler';
import type { ThemePack } from '@/types/theme-packs.types';

// undefined leaves a field as it is; null clears the theme, which the editor can remove
type CustomizationsPatch = Omit<Partial<IScheduledEmailContentV2>, 'themeColorPackId' | 'themeColorPack'> & {
  themeColorPackId?: IScheduledEmailContentV2['themeColorPackId'] | null;
  themeColorPack?: IScheduledEmailContentV2['themeColorPack'] | null;
};

export const reducers = (create: ReducerCreators<ScheduledEmailsState>) => ({
  createScheduledEmail: create.asyncThunk(
    async (request: IInsertScheduledEmailRequest, { rejectWithValue }) => {
      try {
        return (await CreateScheduledEmail(request)) as IScheduledEmail;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.creatingEmail = true;
        state.errorCreatingEmail = false;
      },
      fulfilled: (state, action) => {
        state.emails = [action.payload, ...(state.emails ?? [])];
        // creating inserts a published row too
        state.publishedEmails = null;
        state.creatingEmail = false;
        state.errorCreatingEmail = false;
      },
      rejected: (state) => {
        state.creatingEmail = false;
        state.errorCreatingEmail = true;
      },
    }
  ),

  loadEventScheduledEmails: create.asyncThunk(
    async (event_uuid: string, { rejectWithValue }) => {
      try {
        return (await GetEventScheduledEmails(event_uuid)) as IScheduledEmail[];
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.loadingEmails = true;
        state.errorLoadingEmails = false;
      },
      fulfilled: (state, action) => {
        state.emails = action.payload ?? [];
        state.loadingEmails = false;
        state.errorLoadingEmails = false;
      },
      rejected: (state) => {
        state.loadingEmails = false;
        state.errorLoadingEmails = true;
      },
    }
  ),

  loadEventPublishedScheduledEmails: create.asyncThunk(
    async (event_uuid: string, { rejectWithValue }) => {
      try {
        return (await GetEventPublishedScheduledEmails(event_uuid)) as IScheduledEmail[];
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state, action) => {
        // a different event's emails mustn't show while this event's load
        if (action.meta.arg !== state.publishedEmailsEventUuid) {
          state.publishedEmails = null;
        }
        state.publishedEmailsEventUuid = action.meta.arg;
        state.loadingPublishedEmails = true;
        state.errorLoadingPublishedEmails = false;
      },
      fulfilled: (state, action) => {
        // a load for an event that's since been switched away from
        if (action.meta.arg !== state.publishedEmailsEventUuid) return;
        state.publishedEmails = action.payload ?? [];
        state.loadingPublishedEmails = false;
        state.errorLoadingPublishedEmails = false;
      },
      rejected: (state, action) => {
        if (action.meta.arg !== state.publishedEmailsEventUuid) return;
        state.loadingPublishedEmails = false;
        state.errorLoadingPublishedEmails = true;
      },
    }
  ),

  loadChannelScheduledEmails: create.asyncThunk(
    async (channel: number, { rejectWithValue }) => {
      try {
        return (await GetChannelScheduledEmails(channel)) as IScheduledEmail[];
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.loadingEmails = true;
        state.errorLoadingEmails = false;
      },
      fulfilled: (state, action) => {
        state.emails = action.payload ?? [];
        state.loadingEmails = false;
        state.errorLoadingEmails = false;
      },
      rejected: (state) => {
        state.loadingEmails = false;
        state.errorLoadingEmails = true;
      },
    }
  ),

  setActiveEmail: create.reducer((state, action: PayloadAction<IScheduledEmail>) => {
    state.activeEmail = action.payload;
  }),

  clearActiveEmail: create.reducer((state) => {
    state.activeEmail = null;
  }),

  updateScheduledEmail: create.asyncThunk(
    async (email: IScheduledEmail, { rejectWithValue }) => {
      try {
        return (await EditScheduledEmail(email)) as IScheduledEmail;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.updatingEmail = true;
        state.errorUpdatingEmail = false;
      },
      fulfilled: (state, action) => {
        state.updatingEmail = false;
        state.errorUpdatingEmail = false;
        state.emails = (state.emails ?? []).map((e) => (e.uuid === action.payload.uuid ? action.payload : e));
        if (state.activeEmail?.uuid === action.payload.uuid) {
          state.activeEmail = action.payload;
          state.activeEmail.draft = true;
        }
      },
      rejected: (state) => {
        state.updatingEmail = false;
        state.errorUpdatingEmail = true;
      },
    }
  ),
  publishScheduledEmail: create.asyncThunk(
    async (email: IScheduledEmail, { rejectWithValue }) => {
      try {
        return (await PublishScheduledEmail(email.uuid, email.channel)) as IScheduledEmail;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.updatingEmail = true;
        state.errorUpdatingEmail = false;
      },
      fulfilled: (state, action) => {
        state.updatingEmail = false;
        state.errorUpdatingEmail = false;
        const uuid = action.payload.uuid;
        state.emails?.forEach((email) => {
          if (email.uuid === uuid) {
            email.draft = false;
          }
        });
        // the published version changed - reload it the next time it's read
        state.publishedEmails = null;
        // discard published email response body - it does not have the correct audiences array and it's not
        // necessary to keep it right now
      },
      rejected: (state) => {
        state.updatingEmail = false;
        state.errorUpdatingEmail = true;
      },
    }
  ),

  patchScheduledEmailFields: create.reducer(
    (
      state,
      action: PayloadAction<{
        uuid: string;
        fields: Partial<
          Pick<
            IScheduledEmail,
            'delivery_date' | 'timezone' | 'audiences' | 'email_type' | 'settings' | 'dynamic_audiences'
          >
        >;
      }>
    ) => {
      const { uuid, fields } = action.payload;
      const email = (state.emails ?? []).find((e) => e.uuid === uuid);
      if (!email) return;
      Object.assign(email, fields);
      if (state.activeEmail?.uuid === uuid) {
        Object.assign(state.activeEmail, fields);
      }
    }
  ),

  // the patch is applied to the store immediately and persisted in the background, so the editor
  // never waits on the network - see applyCustomizationsPatch and scheduleCustomizationsSave
  patchScheduledEmailCustomizations: create.asyncThunk(
    async ({ uuid }: { uuid: string; fields: CustomizationsPatch }, { getState, rejectWithValue }) => {
      try {
        // read the email only once the save actually goes out, so it carries every patch that
        // landed while this one was waiting rather than just the fields this action supplied
        const saved = await scheduleCustomizationsSave(uuid, async () => {
          const email = (getState() as RootState).scheduledEmails.emails?.find((e) => e.uuid === uuid);
          if (!email) return;
          await EditScheduledEmail(email);
        });
        return { uuid, saved };
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      options: {
        condition: ({ uuid }, { getState }) =>
          !!(getState() as RootState).scheduledEmails.emails?.some((e) => e.uuid === uuid),
      },
      // optimistic: the patch lands before the request is even made
      pending: (state, action) => {
        applyCustomizationsPatch(state, action.meta.arg);
        state.pendingCustomizationSaves += 1;
        state.errorSavingCustomizations = false;
      },
      // the response is deliberately not written back into state - the user has very likely typed
      // more since this request went out, and the store already holds the newer content
      fulfilled: (state) => {
        state.pendingCustomizationSaves = Math.max(0, state.pendingCustomizationSaves - 1);
      },
      rejected: (state) => {
        state.pendingCustomizationSaves = Math.max(0, state.pendingCustomizationSaves - 1);
        state.errorSavingCustomizations = true;
      },
    }
  ),

  deleteScheduledEmail: create.asyncThunk(
    async (uuid: string, { rejectWithValue }) => {
      try {
        await DeleteScheduledEmail(uuid);
        return uuid;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state, action) => {
        state.deletingEmailUuid = action.meta.arg;
        state.errorDeletingEmail = false;
      },
      fulfilled: (state, action) => {
        state.emails = (state.emails ?? []).filter((e) => e.uuid !== action.payload);
        state.publishedEmails = state.publishedEmails?.filter((e) => e.uuid !== action.payload) ?? null;
        if (state.activeEmail?.uuid === action.payload) {
          state.activeEmail = null;
        }
        state.deletingEmailUuid = null;
        state.errorDeletingEmail = false;
      },
      rejected: (state) => {
        state.deletingEmailUuid = null;
        state.errorDeletingEmail = true;
      },
    }
  ),
});

const themePackToEssentials = (
  themePack: ThemePack | ThemePackCampaignEssentials | null | undefined
): ThemePackCampaignEssentials | undefined => {
  if (!themePack) return undefined;

  return {
    title: themePack.title,
    color_pack: themePack.color_pack,
    colors: themePack.colors,
    font_pack: themePack.font_pack
      ? {
          heading_name: themePack.font_pack.heading_name,
          heading_url: themePack.font_pack.heading_url,
          body_name: themePack.font_pack.body_name,
          body_url: themePack.font_pack.body_url,
        }
      : undefined,
    custom_styles: themePack.custom_styles,
    theme_mode: themePack.theme_mode,
  };
};

function applyCustomizationsPatch(
  state: Draft<ScheduledEmailsState>,
  { uuid, fields }: { uuid: string; fields: CustomizationsPatch }
) {
  const email = (state.emails ?? []).find((e) => e.uuid === uuid);
  if (!email) return;
  const existingV2: IScheduledEmailContentV2 =
    (email.customizations.v2 as IScheduledEmailContentV2 | undefined) ?? defaultCustomizations;

  const patched: IScheduledEmailContentV2 = {
    ...existingV2,
    ...(fields.title !== undefined ? { title: fields.title } : {}),
    ...(fields.salutation !== undefined ? { salutation: fields.salutation } : {}),
    ...(fields.description !== undefined ? { description: fields.description } : {}),
    ...(fields.startIntro !== undefined ? { startIntro: fields.startIntro } : {}),
    ...(fields.cta !== undefined ? { cta: fields.cta } : {}),
    ...(fields.subject !== undefined ? { subject: fields.subject } : {}),
    ...(fields.mainImage !== undefined ? { mainImage: fields.mainImage } : {}),
    ...(fields.headingImage !== undefined ? { headingImage: fields.headingImage } : {}),
    ...(fields.secondaryImage !== undefined ? { secondaryImage: fields.secondaryImage } : {}),
    ...(fields.backgroundColor !== undefined ? { backgroundColor: fields.backgroundColor } : {}),
    ...(fields.containerColor !== undefined ? { containerColor: fields.containerColor } : {}),
    ...(fields.secondaryAccentColor !== undefined ? { secondaryAccentColor: fields.secondaryAccentColor } : {}),
    ...(fields.secondaryBackgroundColor !== undefined
      ? { secondaryBackgroundColor: fields.secondaryBackgroundColor }
      : {}),
    ...(fields.accentColor !== undefined ? { accentColor: fields.accentColor } : {}),
    ...(fields.headingTextColor !== undefined ? { headingTextColor: fields.headingTextColor } : {}),
    ...(fields.bodyTextColor !== undefined ? { bodyTextColor: fields.bodyTextColor } : {}),
    ...(fields.buttonTextColor !== undefined ? { buttonTextColor: fields.buttonTextColor } : {}),
    ...(fields.templateId !== undefined ? { templateId: fields.templateId } : {}),
    ...(fields.campaignTitle !== undefined ? { campaignTitle: fields.campaignTitle } : {}),
    ...(fields.themeColorPackId !== undefined ? { themeColorPackId: fields.themeColorPackId ?? undefined } : {}),
    ...(fields.themeColorPack !== undefined
      ? { themeColorPack: themePackToEssentials(fields.themeColorPack ?? undefined) }
      : {}),
    ...(fields.themeMode !== undefined ? { themeMode: fields.themeMode } : {}),
    ...(fields.hideBrandliveLogo !== undefined ? { hideBrandliveLogo: fields.hideBrandliveLogo } : {}),
    // a block's id/styling only make sense against the template it was seeded for - if this
    // patch switches templateId without also supplying a fresh blocks array for the new
    // template, the old template's blocks must be dropped rather than carried forward,
    // otherwise the new template renders the old one's content under mismatched ids
    ...(fields.blocks !== undefined
      ? { blocks: fields.blocks }
      : fields.templateId !== undefined && fields.templateId !== existingV2.templateId
        ? { blocks: undefined }
        : {}),
  };

  if (!email.working_customizations) email.working_customizations = {};
  (email.working_customizations.v2 as IScheduledEmailContentV2) = patched;

  (email.customizations.v2 as IScheduledEmailContentV2) = patched;

  // the save will make the API set this, but the draft badge should flip on the edit itself
  email.draft = true;

  if (state.activeEmail?.uuid === uuid) {
    if (!state.activeEmail.working_customizations) state.activeEmail.working_customizations = {};
    (state.activeEmail.working_customizations.v2 as IScheduledEmailContentV2) = patched;
    (state.activeEmail.customizations.v2 as IScheduledEmailContentV2) = patched;
    state.activeEmail.draft = true;
  }
}
